<template>
	<div
		class="flow-table-component"
		:style="tableWrapperStyle"
		:data-border="border"
		:data-row-gap="rowGap > 0"
		:data-column-gap="columnGap > 0"
	>
		<table :style="tableStyle">
			<thead>
				<slot name="header"> </slot>
			</thead>
			<tbody>
				<slot name="body"> </slot>
			</tbody>
		</table>
	</div>
</template>
<script lang="ts">
import { StyleValue } from "./table-types";

import { defineComponent } from "vue";

export default defineComponent({
	name: "Table",

	props: {
		background: {
			type: String,
			default: () => "var(--gray-600)"
		},

		border: {
			type: String,
			default: () => "all",

			validator(value: string) {
				return ["all", "rows", "columns", "table", "header", "none"].some(v => value.includes(v));
			}
		},

		rowGap: {
			type: Number,
			default: () => 0
		},

		columnGap: {
			type: Number,
			default: () => 0
		},

		height: {
			type: String,
			default: () => null
		}
	},

	computed: {
		tableWrapperStyle(): StyleValue {
			return {
				"--table-background": this.background,
				height: this.height
			};
		},

		tableStyle(): StyleValue {
			let borderCollapse = "collapse";
			const borderSpacing = `${this.columnGap}px ${this.rowGap}px`;
			if (this.rowGap > 0 || this.columnGap > 0) {
				borderCollapse = "separate";
			}
			return {
				borderCollapse,
				borderSpacing
			};
		}
	}
});
</script>
<style lang="scss">
div.flow-table-component {
	overflow: auto;

	display: flex;
	justify-content: flex-start;
	align-items: flex-start;
	flex-direction: column;

	flex: 1 0 auto;
	&[data-border="none"] {
		border: none;
		th,
		td {
			border: none;
		}
	}

	&[data-border*="all"] {
		border: 1px solid var(--gray-700);
		border-radius: 4px;

		&[data-row-gap] {
			border: none;
			border-radius: 0px;
			th,
			td {
				border-left: 1px solid var(--gray-700);
				border-top: 1px solid var(--gray-700);
				border-bottom: 1px solid var(--gray-700);
				&:last-child {
					border-right: 1px solid var(--gray-700);
				}
			}

			tbody tr:last-child {
				th,
				td {
					border-bottom: 1px solid var(--gray-700);
				}
			}
		}
		&[data-column-gap] {
			tr {
				th,
				td {
					border-right: 1px solid var(--gray-700);
				}
			}
		}
		th,
		td {
			border-bottom: 1px solid var(--gray-700);
			&:not(:first-child) {
				border-left: 1px solid var(--gray-700);
			}
		}
		tbody tr:last-child {
			th,
			td {
				border-bottom: none;
			}
		}
	}
	&[data-border*="rows"] {
		th,
		td {
			border-bottom: 1px solid var(--gray-700);
		}

		&[data-row-gap] {
			th,
			td {
				border-bottom: 1px solid var(--gray-700);
				border-top: 1px solid var(--gray-700);

				&:first-child {
					border-left: 1px solid var(--gray-700);
				}
				&:last-child {
					border-right: 1px solid var(--gray-700);
				}
			}
		}

		&[data-column-gap] {
			tr {
				th,
				td {
					border-left: 1px solid var(--gray-700);
					border-right: 1px solid var(--gray-700);
				}
			}
		}
	}

	&[data-border*="columns"] {
		th,
		td {
			&:not(:first-child) {
				border-left: 1px solid var(--gray-700);
			}
		}

		&[data-column-gap] {
			th,
			td {
				border-right: 1px solid var(--gray-700);
				&:first-child {
					border-left: 1px solid var(--gray-700);
				}
			}

			tr:last-child {
				td {
					border-bottom: 1px solid var(--gray-700);
				}
			}
			tr:first-child {
				th {
					border-top: 1px solid var(--gray-700);
				}
			}
		}
		&[data-row-gap] {
			tr {
				th,
				td {
					border-bottom: 1px solid var(--gray-700);
					border-top: 1px solid var(--gray-700);
				}
			}
		}
	}

	&[data-border*="table"] {
		border: 1px solid var(--gray-700);
		border-radius: 4px;
		tbody tr:last-child {
			th,
			td {
				border-bottom: none;
			}
		}
	}
	&[data-border*="header"] {
		thead {
			th,
			td {
				border-bottom: 1px solid var(--gray-700);
			}
		}
		&[data-row-gap][data-column-gap] {
			thead {
				th,
				td {
					border: 1px solid var(--gray-700);
				}
			}
		}
	}
	table {
		border-collapse: collapse;
		width: 100%;

		thead {
			position: sticky;
			top: 0px;

			td,
			th {
				background: var(--table-background);
			}
		}

		td,
		th {
			text-align: left;
			padding: 12px;
		}
	}
	.hover {
		background: var(--gray-500) !important;
	}

	tr,
	th,
	td {
		.show-on-hover {
			display: none !important;
		}

		&:hover {
			.show-on-hover {
				display: flex !important;
			}
		}
	}
}
</style>
